import { useState, useEffect } from "react";
import axios from "@helpers/axios/private.axios";
import { alpha3ToAlpha2 } from "@utils/countryCodeMap";

export const useCountries = (locale) => {
  const [countries, setCountries] = useState([]);
  const [countryAutocompleteOpen, setCountryAutocompleteOpen] = useState(false);

  useEffect(() => {
    const getCountries = async () => {
      try {
        const resp = await axios.get("/common/countries");
        setCountries(resp.data.data);
      } catch (e) {
        // Failed to load countries
      }
    };
    getCountries();
  }, []);

  const getCountryName = (countryCode) => {
    if (!countryCode || !countries.length) return null;
    const raw = typeof countryCode === "string" ? countryCode.trim() : "";
    if (!raw) return null;

    const alpha2 =
      raw.length === 2
        ? raw.toUpperCase()
        : raw.length === 3
          ? (alpha3ToAlpha2[raw.toUpperCase()] || "").toUpperCase()
          : "";

    const normalizeAlpha2 = (code) => {
      if (!code || typeof code !== "string") return "";
      const trimmed = code.trim();
      if (trimmed.length !== 2) return "";
      return trimmed.toUpperCase();
    };

    const deriveAlpha2FromCountryItem = (c) => {
      const direct =
        normalizeAlpha2(c.code_alpha2) ||
        normalizeAlpha2(c.alpha2) ||
        normalizeAlpha2(c.iso2) ||
        normalizeAlpha2(c.code2) ||
        normalizeAlpha2(c.code);
      if (direct) return direct;

      // If API stores alpha-3 in `code`, map it.
      if (typeof c.code === "string" && c.code.trim().length === 3) {
        const mapped = alpha3ToAlpha2[c.code.trim().toUpperCase()];
        return normalizeAlpha2(mapped) || "";
      }

      return "";
    };

    const countryData =
      // exact matches first
      countries.find((c) => c.code === raw) ||
      countries.find(
        (c) =>
          c.code_alpha2 === raw ||
          c.alpha2 === raw ||
          c.iso2 === raw ||
          c.code2 === raw,
      ) ||
      // alpha-2-aware matching (handles `code` as alpha-3 too)
      (alpha2
        ? countries.find((c) => deriveAlpha2FromCountryItem(c) === alpha2)
        : null);
    if (!countryData) return null;
    return locale === "ru" ? countryData.label_ru : countryData.label_en;
  };

  return {
    countries,
    countryAutocompleteOpen,
    setCountryAutocompleteOpen,
    getCountryName,
  };
};